import { IRequirementResponse } from "@wingspanhq/payments/dist/interfaces";
import {
  useWSSnackbar,
  WSPageToolbarAction
} from "@wingspanhq/fe-component-library";
import {
  ContextItem,
  RequirementAction,
  RequirementAdditionalData,
  RequirementContextType,
  RequirementStatusViewProps,
  RequirementTableCallback
} from "../types";
import { useMarkRequirementAsCompleteModal } from "../components/MarkRequirementAsCompleteModal";
import { useMarkRequirementAsIncompleteModal } from "../components/MarkRequirementAsIncompleteModal";
import { useReRequestRequirementModal } from "../components/ReRequestRequirementModal";
import { RequirementStatus } from "@wingspanhq/payments/dist/interfaces/requirement";
import { useRevertRequirementModal } from "../components/RevertRequirementModal";
import { ALL_REQUIREMENT_EVENTS } from "../utils";
import { selectorIsRequirementPostApproval } from "../selectors/selectorIsRequirementPostApproval";

export const useRequirementDefaultActions = (
  contextType: RequirementContextType,
  callback: RequirementTableCallback
) => {
  const { openSnackbar } = useWSSnackbar();
  const markRequirementAsCompleteModal = useMarkRequirementAsCompleteModal();
  const markRequirementAsIncompleteModal =
    useMarkRequirementAsIncompleteModal();
  const reRequestRequirementModal = useReRequestRequirementModal();
  const revertRequirementModal = useRevertRequirementModal();

  const handleRevertRequirementStatus =
    (
      requirement: IRequirementResponse,
      context: ContextItem,
      additionalData: RequirementAdditionalData,
      currentStatus: RequirementStatusViewProps
    ) =>
    () => {
      revertRequirementModal.open({
        requirement,
        context,
        additionalData,
        onSuccess() {
          callback(ALL_REQUIREMENT_EVENTS.ResetStatus, requirement);
          openSnackbar({
            message: "Requirement status reverted successfully",
            duration: 3000,
            type: "success"
          });
        },
        currentStatus
      });
    };

  const handleMarkRequirementAsCompleted =
    (
      requirement: IRequirementResponse,
      context: ContextItem,
      additionalData: RequirementAdditionalData,
      currentStatus: RequirementStatusViewProps
    ) =>
    () => {
      markRequirementAsCompleteModal.open({
        requirement,
        context,
        additionalData,
        onSuccess() {
          callback(ALL_REQUIREMENT_EVENTS.OverrideStatus, requirement);
          openSnackbar({
            message: "Requirement marked as complete successfully",
            duration: 3000,
            type: "success"
          });
        },
        currentStatus
      });
    };

  const handleMarkRequirementAsIncomplete =
    (
      requirement: IRequirementResponse,
      context: ContextItem,
      additionalData: RequirementAdditionalData,
      currentStatus: RequirementStatusViewProps
    ) =>
    () => {
      markRequirementAsIncompleteModal.open({
        requirement,
        context,
        additionalData,
        onSuccess() {
          callback(ALL_REQUIREMENT_EVENTS.OverrideStatus, requirement);
          openSnackbar({
            message: "Requirement marked as incomplete successfully",
            duration: 3000,
            type: "success"
          });
        },
        currentStatus
      });
    };

  const handleReRequestRequirement =
    (
      requirement: IRequirementResponse,
      context: ContextItem,
      additionalData: RequirementAdditionalData,
      currentStatus: RequirementStatusViewProps
    ) =>
    () => {
      reRequestRequirementModal.open({
        requirement,
        context,
        additionalData,
        onSuccess() {
          callback(ALL_REQUIREMENT_EVENTS.OverrideStatus, requirement);
          openSnackbar({
            message: "Requirement re-requested successfully",
            duration: 3000,
            type: "success"
          });
        },
        currentStatus
      });
    };

  return (
    requirement: IRequirementResponse,
    context: ContextItem,
    additionalDataItem: RequirementAdditionalData,
    currentStatus: RequirementStatusViewProps
  ): {
    extendToolbarActions(actions: RequirementAction[]): RequirementAction[];
  } => {
    if (
      "requirementId" in requirement &&
      contextType === RequirementContextType.Payee
    ) {
      const labelAction: WSPageToolbarAction = {
        groupLabel: "Manage requirement"
      };

      const reRequestAction: RequirementAction = {
        label: "Re-request",
        icon: "reset",
        helpText:
          "Resets the status to incomplete and the contractor starts the requirement from scratch.",
        onClick: handleReRequestRequirement(
          requirement,
          context,
          additionalDataItem,
          currentStatus
        )
      };

      const isReadyForApprove =
        requirement.status === RequirementStatus.PendingReview;

      const isPostApproval = selectorIsRequirementPostApproval(requirement);

      const defaultActions: RequirementAction[] = [
        {
          label: "Approve (mark complete)",
          buttonLabel: "Approve",
          buttonKind: isPostApproval ? undefined : "Primary",
          buttonOrder: 2,
          icon: "accept",
          buttonIcon: null,
          helpText:
            "Approves the submission and marks the requirement as complete and overwrites current status.",
          onClick: handleMarkRequirementAsCompleted(
            requirement,
            context,
            additionalDataItem,
            currentStatus
          ),
          visible:
            isReadyForApprove ||
            (isPostApproval &&
              requirement.status !== RequirementStatus.Complete),
          mainAction: true
        },
        {
          label: "Reject (mark incomplete)",
          buttonLabel: "Reject",
          buttonIcon: null,
          buttonKind: isPostApproval ? undefined : "Secondary",
          destructive: true,
          helpText:
            "Rejects the submission and marks the requirements as incomplete and overwrites current status.",
          icon: "dispute",
          onClick: handleMarkRequirementAsIncomplete(
            requirement,
            context,
            additionalDataItem,
            currentStatus
          ),
          visible:
            isReadyForApprove ||
            (isPostApproval &&
              requirement.status !== RequirementStatus.Incomplete),
          mainAction: true
        },
        {
          label: "Mark complete",
          icon: "accept",
          buttonIcon: null,
          helpText:
            "Overwritten status will be updated for both you and the contractor",
          onClick: handleMarkRequirementAsCompleted(
            requirement,
            context,
            additionalDataItem,
            currentStatus
          ),
          visible:
            !isReadyForApprove &&
            !isPostApproval &&
            requirement.payerOverrideStatus !== RequirementStatus.Complete
        },
        {
          label: "Mark incomplete",
          buttonIcon: null,
          helpText:
            "Overwritten status will be updated for both you and the contractor",
          icon: "dispute",
          onClick: handleMarkRequirementAsIncomplete(
            requirement,
            context,
            additionalDataItem,
            currentStatus
          ),
          visible:
            !isReadyForApprove &&
            !isPostApproval &&
            requirement.payerOverrideStatus !== RequirementStatus.Incomplete
        },
        {
          label: "Revert to synced status",
          buttonIcon: null,
          helpText: "Undo overwritten status and revert to synced status",
          icon: "undo",
          onClick: handleRevertRequirementStatus(
            requirement,
            context,
            additionalDataItem,
            currentStatus
          ),
          visible:
            !isReadyForApprove &&
            !isPostApproval &&
            !!requirement?.payerOverrideStatus
        }
      ];

      const actions = [labelAction, ...defaultActions, reRequestAction];

      return {
        extendToolbarActions: originalActions => {
          return [...originalActions, ...actions];
        }
      };
    }

    return {
      extendToolbarActions(actions) {
        return actions;
      }
    };
  };
};
